/**
 * UserMenu + PinUnlockedBadge — extracted from AppShell so both the desktop
 * sidebar (bottom) and the mobile top bar can share one implementation.
 */
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, LogOut, User, Users, Wrench } from 'lucide-react';
import { api } from '../api';
import { useWhoami } from '../lib/auth';
import { Pill } from './primitives';

export function PinUnlockedBadge({ unlockedUntil }: { unlockedUntil: string | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!unlockedUntil) return;
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, [unlockedUntil]);
  if (!unlockedUntil) return null;
  const remainingMs = new Date(unlockedUntil).getTime() - now;
  if (remainingMs <= 0) return null;
  const minutes = Math.floor(remainingMs / 60_000);
  const seconds = Math.floor((remainingMs % 60_000) / 1000);
  return (
    <Pill tone="green">
      PIN {minutes}:{seconds.toString().padStart(2, '0')}
    </Pill>
  );
}

export function UserMenu({
  align = 'right',
  tone = 'light',
  compact = false,
}: {
  align?: 'right' | 'left';
  tone?: 'light' | 'dark';
  compact?: boolean;
}) {
  const who = useWhoami();
  const nav = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const logoutMut = useMutation({
    mutationFn: () => api.post<unknown>('/api/auth/logout'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['whoami'] });
      nav('/login', { replace: true });
    },
  });

  if (who.data?.state !== 'authenticated') return null;
  const isOwner = who.data.role === 'owner';
  const username = who.data.username;
  const initial = username.slice(0, 1).toUpperCase();
  const menuClass =
    tone === 'dark'
      ? 'absolute bottom-full z-40 mb-1 min-w-[200px] overflow-hidden rounded-card border border-border bg-surface py-1 shadow-[0_4px_16px_rgba(0,0,0,0.1)]'
      : 'app-shell-surface absolute top-full z-40 mt-2 min-w-[220px] overflow-hidden rounded-[22px] border border-border py-1 shadow-[0_16px_36px_-24px_rgba(15,23,42,0.38)] backdrop-blur-xl';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          tone === 'dark'
            ? 'inline-flex w-full items-center gap-1.5 rounded-md border px-2 py-1.5 text-small transition-colors hover:bg-[#16273e]'
            : compact
            ? 'inline-flex h-11 min-w-[48px] items-center gap-1.5 rounded-full border border-border bg-surface px-2 text-small font-semibold text-ink shadow-sm backdrop-blur transition-colors hover:bg-surface-raised'
            : 'inline-flex w-full items-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-small text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink'
        }
        style={
          tone === 'dark'
            ? { borderColor: 'var(--sidebar-border)', color: 'var(--sidebar-text)' }
            : undefined
        }
      >
        {compact ? (
          <>
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[rgba(16,26,45,0.96)] text-[13px] font-semibold text-white shadow-sm">
              {initial}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} />
          </>
        ) : (
          <>
            <User className="h-4 w-4" strokeWidth={1.75} />
            <span className="min-w-0 flex-1 truncate text-left">{username}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
          </>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className={[
            menuClass,
            align === 'right' ? 'right-0' : 'left-0',
          ].join(' ')}
        >
          <div className="border-b border-border px-3 py-2.5">
            <div className="text-caption font-semibold uppercase tracking-[0.12em] text-ink-muted">
              Signed in
            </div>
            <div className="mt-1 text-small font-semibold text-ink">{username}</div>
            <div className="text-caption text-ink-muted">{isOwner ? 'Owner access' : 'Boss access'}</div>
          </div>
          <MenuItem to="/account" icon={User} label="My account" />
          <MenuItem to="/wizard" icon={Wrench} label="Setup wizard" />
          {isOwner && <MenuItem to="/settings/users" icon={Users} label="Users" />}
          <div className="my-1 h-px bg-border" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logoutMut.mutate();
            }}
            className="flex w-full min-h-[44px] items-center gap-2 px-3 py-2 text-small text-neg transition-colors hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.75} />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      className="flex w-full min-h-[44px] items-center gap-2 px-3 py-2 text-small text-ink transition-colors hover:bg-surface-raised"
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} />
      <span>{label}</span>
    </NavLink>
  );
}
